// splineUtils.js — Shared Catmull-Rom spline utilities for path/lane editing.

/** Samples per segment used for edit-mode hit detection and rendering. */
export const SPLINE_SAMPLES_EDIT = 12;

// Centripetal exponent. alpha=0.5 is the centripetal parameterization, which is
// proven to never cusp or self-intersect — unlike uniform (alpha=0) Catmull-Rom,
// which loops/overshoots when control points are unevenly spaced.
const CENTRIPETAL_ALPHA = 0.5;
// Floor for a knot interval so coincident/near-coincident points can't divide by
// zero. Keeps output finite and preserves the per-segment sample count.
const KNOT_EPSILON = 1e-9;

const lerp = (a, b, u) => ({
  lat: a.lat + (b.lat - a.lat) * u,
  lng: a.lng + (b.lng - a.lng) * u,
});

// Chord length with cosine-corrected longitude so the knot weighting isn't
// skewed by latitude (a degree of lng is shorter than a degree of lat away from
// the equator). Used only for relative knot spacing, not for display.
function knotChord(a, b) {
  const latRad = (((a.lat + b.lat) / 2) * Math.PI) / 180;
  const dLat = b.lat - a.lat;
  const dLng = (b.lng - a.lng) * Math.cos(latRad);
  return Math.hypot(dLat, dLng);
}

// Control point with reflected phantoms at the ends so the first/last segments
// have a well-defined, natural tangent (and a non-zero end knot interval).
function controlAt(points, i) {
  const n = points.length;
  if (i < 0) {
    return {
      lat: 2 * points[0].lat - points[1].lat,
      lng: 2 * points[0].lng - points[1].lng,
    };
  }
  if (i >= n) {
    return {
      lat: 2 * points[n - 1].lat - points[n - 2].lat,
      lng: 2 * points[n - 1].lng - points[n - 2].lng,
    };
  }
  return points[i];
}

/**
 * Centripetal Catmull-Rom spline through `points`; returns the interpolated
 * path (lat/lng). The curve passes through every control point.
 *
 * Drop-in contract (relied on by callers and the lane editor's segment math):
 *   - same signature and { lat, lng }[] in/out
 *   - emits EXACTLY `numSamplesPerSegment` points per segment, then appends the
 *     final endpoint → length === (n-1)*numSamplesPerSegment + 1 for n >= 2
 *   - out[i * numSamplesPerSegment] === points[i] (each segment starts on its node)
 *
 * @param points - Array of { lat, lng }
 * @param numSamplesPerSegment - samples per segment (default 16)
 * @returns Array of { lat, lng } along the curve
 */
export function catmullRomSpline(points, numSamplesPerSegment = 16) {
  if (!points?.length) return [];
  if (points.length === 1) return [points[0]];
  const out = [];
  const n = points.length;
  for (let i = 0; i < n - 1; i++) {
    const p0 = controlAt(points, i - 1);
    const p1 = controlAt(points, i);
    const p2 = controlAt(points, i + 1);
    const p3 = controlAt(points, i + 2);

    // Centripetal knot sequence.
    const t0 = 0;
    const t1 = t0 + Math.max(KNOT_EPSILON, Math.pow(knotChord(p0, p1), CENTRIPETAL_ALPHA));
    const t2 = t1 + Math.max(KNOT_EPSILON, Math.pow(knotChord(p1, p2), CENTRIPETAL_ALPHA));
    const t3 = t2 + Math.max(KNOT_EPSILON, Math.pow(knotChord(p2, p3), CENTRIPETAL_ALPHA));

    for (let j = 0; j < numSamplesPerSegment; j++) {
      // Sample uniformly within this segment's knot span [t1, t2).
      const t = t1 + (j / numSamplesPerSegment) * (t2 - t1);
      // Barry–Goldman pyramidal evaluation of the non-uniform Catmull-Rom.
      const a1 = lerp(p0, p1, (t - t0) / (t1 - t0));
      const a2 = lerp(p1, p2, (t - t1) / (t2 - t1));
      const a3 = lerp(p2, p3, (t - t2) / (t3 - t2));
      const b1 = lerp(a1, a2, (t - t0) / (t2 - t0));
      const b2 = lerp(a2, a3, (t - t1) / (t3 - t1));
      out.push(lerp(b1, b2, (t - t1) / (t2 - t1)));
    }
  }
  out.push(points[n - 1]);
  return out;
}

/**
 * Douglas-Peucker simplification of projected [x, y] points, tolerance in the
 * same (world) units. Endpoints are always kept. Iterative (stack-based), so
 * multi-thousand-point routes can't blow the call stack.
 *
 * `maxPoints` bounds the output size for request payloads: when the result
 * exceeds it, the tolerance doubles and the pass reruns (a coarser polyline is
 * fine for consumers with their own slack, e.g. the lane-trace route fence).
 *
 * @param points - Array of [x, y]
 * @param tolerance - max allowed deviation, world units (> 0)
 * @param maxPoints - optional output size cap (default unbounded)
 * @returns Array of [x, y] (new array; inputs with < 3 points pass through)
 */
export function simplifyPolylineXY(points, tolerance, maxPoints = Infinity) {
  if (!Array.isArray(points) || points.length < 3) {
    return Array.isArray(points) ? points.slice() : [];
  }
  let tol = Math.max(1e-9, tolerance);
  for (let round = 0; round < 12; round++) {
    const keep = new Array(points.length).fill(false);
    keep[0] = keep[points.length - 1] = true;
    const stack = [[0, points.length - 1]];
    while (stack.length) {
      const [i0, i1] = stack.pop();
      const [ax, ay] = points[i0];
      const [bx, by] = points[i1];
      const dx = bx - ax;
      const dy = by - ay;
      const segLen = Math.hypot(dx, dy);
      let dmax = -1;
      let imax = -1;
      for (let i = i0 + 1; i < i1; i++) {
        const [px, py] = points[i];
        const d =
          segLen < 1e-12
            ? Math.hypot(px - ax, py - ay)
            : Math.abs(dy * px - dx * py + bx * ay - by * ax) / segLen;
        if (d > dmax) {
          dmax = d;
          imax = i;
        }
      }
      if (dmax > tol && imax !== -1) {
        keep[imax] = true;
        stack.push([i0, imax], [imax, i1]);
      }
    }
    const out = points.filter((_, i) => keep[i]);
    if (out.length <= maxPoints) return out;
    tol *= 2;
  }
  // Tolerance doublings exhausted (pathological zig-zag): decimate uniformly.
  const step = Math.ceil(points.length / Math.max(2, maxPoints - 1));
  const out = points.filter((_, i) => i % step === 0);
  if (out[out.length - 1] !== points[points.length - 1]) {
    out.push(points[points.length - 1]);
  }
  return out;
}
